import React, { useState, useEffect, useCallback } from 'react';
import styles from './StudentGraduation.module.scss';
import PageTitle from '../../../components/pageTitle/PageTitle';
import Search from '../../../components/search/Search';
import Table from '../../../components/table/Table';
import DropDownButton from '../../../components/dropDownButton/DropDownButton';
import ProfilePicture from '../../../components/profilePicture/ProfilePicture';
import Tag from '../../../components/tag/Tag';
import Filter from '../../../components/filter/Filter';
import Modal from '../../../components/modal/Modal';
import Button from '../../../components/button/Button';
import Icon from '../../../components/icon/Icon';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import { useProgram } from '../../../contexts/ProgramContext';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';

const GRADUATION_FILTERS = [
  { label: 'Eligible for Graduate', value: false },
  { label: 'Graduated', value: true },
];

const StudentGraduation = () => {
  const { showToast } = useToasts();
  const { programs, selectedProgramId, setSelectedProgramId } = useProgram();
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [isGraduated, setIsGraduated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [showGraduateModal, setShowGraduateModal] = useState(false);
  const [modalSelectedIds, setModalSelectedIds] = useState([]);
  const [isGraduating, setIsGraduating] = useState(false);
  const [graduationAction, setGraduationAction] = useState(true);

  const fetchStudents = useCallback(async () => {
    if (!selectedProgramId) return;
    setIsLoading(true);
    try {
      const params = { isGraduated };
      if (search) params.search = search;
      const response = await axiosClient.get(`/programs/${selectedProgramId}/students/graduation-list`, { params });
      setStudents(response.data?.students || []);
      setSelectedStudentIds([]);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [selectedProgramId, search, isGraduated, showToast]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  const handleOpenModal = (action) => {
    setGraduationAction(action);
    setModalSelectedIds([...selectedStudentIds]);
    setShowGraduateModal(true);
  };

  const handleGraduate = async () => {
    setIsGraduating(true);
    try {
      await axiosClient.patch(`/programs/${selectedProgramId}/graduate-students`, {
        studentIds: selectedStudentIds,
        isGraduated: graduationAction,
      });
      showToast('Students graduated successfully', 'success');
      setShowGraduateModal(false);
      setSelectedStudentIds([]);
      fetchStudents();
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsGraduating(false);
    }
  };

  const programOptions = programs.map(p => ({
    label: p.title,
    onClick: () => setSelectedProgramId(p.id),
  }));

  const selectedProgramTitle = programs.find(p => p.id === selectedProgramId)?.title || 'Select Program';

  const selectedStudents = students.filter(s => selectedStudentIds.includes(s.id));

  const columns = [
    {
      header: 'Student',
      key: 'student',
      render: (row) => (
        <div className={styles.studentCell}>
          <ProfilePicture
            firstName={row.firstName}
            lastName={row.lastName}
            src={row.profilePicture}
            size="xs"
          />
          <span>{row.firstName} {row.lastName}</span>
        </div>
      ),
    },
    {
      header: 'Cumulative Score',
      key: 'cumulativeScore',
      render: (row) => row.cumulativeScore != null ? row.cumulativeScore : '-',
    },
    {
      header: 'Graduation',
      key: 'isGraduated',
      render: (row) => (
        <Tag
          text={row.isGraduated ? 'Graduated' : 'In Progress'}
          variant={row.isGraduated ? 5 : 2}
          rounded
        />
      ),
    },
  ];

  const modalColumns = [
    {
      header: 'Student',
      key: 'student',
      render: (row) => (
        <div className={styles.studentCell}>
          <ProfilePicture
            firstName={row.firstName}
            lastName={row.lastName}
            src={row.profilePicture}
            size="xs"
          />
          <span>{row.firstName} {row.lastName}</span>
        </div>
      ),
    },
    {
      header: 'Cumulative Score',
      key: 'cumulativeScore',
      render: (row) => row.cumulativeScore != null ? row.cumulativeScore : '-',
    },
  ];

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <PageTitle label="Student Graduation" />
          <DropDownButton
            label={selectedProgramTitle}
            items={programOptions}
            type="button"
            size="small"
            variant="primary-light"
            position="bottom-left"
          />
        </div>
        <div className={styles.headerActions}>
          <Search onSubmit={setSearch} />
        </div>
      </div>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <div className={styles.filter}>
            <Filter
              filters={GRADUATION_FILTERS}
              selected={isGraduated}
              onChange={(value) => {
                setIsGraduated(value);
                setSelectedStudentIds([]);
              }}
            />
          </div>
          {selectedStudentIds.length > 0 && (
            <div className={styles.selectionActions}>
              <div className={styles.selectionCount}>
                {selectedStudentIds.length} Selected
              </div>
              <div className={styles.graduateBtnWrapper}>
                {!isGraduated ? (
                  <Button
                    label={
                      <span className={styles.graduateBtn}>
                        <Icon name="graduation-union" />
                        Mark as Graduated
                      </span>
                    }
                    variant="primary"
                    size="small"
                    onClick={() => handleOpenModal(true)}
                  />
                ) : (
                  <Button
                    label={
                      <span className={styles.graduateBtn}>
                        <Icon name="graduation-union" />
                        Remove Graduation
                      </span>
                    }
                    variant="secondary-light"
                    size="small"
                    onClick={() => handleOpenModal(false)}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        <Table
          columns={columns}
          data={students}
          emptyMessage={isGraduated ? "No graduated students found for this program." : "No eligible students found for this program."}
          onRowSelect={setSelectedStudentIds}
          selectedRows={selectedStudentIds}
        />
      </div>

      {showGraduateModal && (
        <Modal>
          {isGraduating && <LoadingOverlay />}
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <div className={styles.modalIcon}>
                <Icon name="graduation-union" />
              </div>
              <div>
                <h3 className={styles.modalTitle}>{graduationAction ? 'Confirm Graduation' : 'Confirm Remove Graduation'}</h3>
                <p className={styles.modalSubtitle}>{graduationAction ? 'This action will graduate selected eligible students.' : 'This action will remove graduation from selected students.'}</p>
              </div>
            </div>
            <div className={styles.modalTable}>
              <Table
                columns={modalColumns}
                data={selectedStudents}
                emptyMessage="No students selected."
              />
            </div>
            <div className={styles.modalActions}>
              <Button
                label="Cancel"
                variant="primary-light"
                size="small"
                onClick={() => setShowGraduateModal(false)}
              />
              <Button
                label={
                  <span className={styles.graduateBtn}>
                    <Icon name="graduation-union" />
                    {graduationAction ? 'Mark as Graduated' : 'Remove Graduation'}
                  </span>
                }
                variant={graduationAction ? 'primary' : 'secondary-light'}
                size="small"
                onClick={handleGraduate}
                disabled={modalSelectedIds.length === 0 || isGraduating}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default StudentGraduation;
